import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Crown,
  Loader2,
  ArrowRight,
  ExternalLink,
  Info,
  X,
  RefreshCw,
  Sparkles,
  AlertTriangle,
  Lock,
  Bot,
  HelpCircle,
  ChevronDown,
  XCircle,
} from 'lucide-react';
import { authAPI, billingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useBilling, useBillingRefreshSource } from '../contexts/BillingContext';
import { useToast } from '../contexts/ToastContext';
import PlanPriceLine from '../components/billing/PlanPriceLine';
import type {
  Plan,
  ProrationPreview,
  UsageSnapshot,
  BillingInterval,
  ActiveCredit,
  BundleResource,
} from '../types/billing';
// `ENTERPRISE_MAILTO` is no longer imported here — Plan-EnterpriseUpsellViaWidget
// §A.2 swaps the mailto CTA for an in-widget chat. The constant stays in
// `types/billing.ts` and is consumed by `utils/widgetControl.ts` as the
// fallback when the Prodagi Help Widget iframe isn't mounted.
import {
  PLAN_DISPLAY,
  PLAN_CARD_LIMITS,
  USAGE_METRICS,
  isPooledBilling,
  formatPlanLimit,
  checkPlanCardLimitDrift,
} from '../types/billing';
import LiveAvatarSection from '../components/billing/LiveAvatarSection';
import BundleCards from '../components/billing/BundleCards';
import SimpleBundleModal from '../components/billing/SimpleBundleModal';
import TextTopUpHistory from '../components/billing/TextTopUpHistory';
import OverageSettingsSection from '../components/billing/OverageSettingsSection';
import UsageBarWithCredits from '../components/billing/UsageBarWithCredits';
import { resolveAvatarMinutes } from '../components/billing/liveAvatarMinutes';
import {
  UsageBar,
  OverrideDisclosure,
  overrideKeyFor,
  TextPoolBar,
  formatTokenCount,
} from '../components/billing/usageBars';
import { openEnterpriseUpgradeChat } from '../utils/widgetControl';
import { formatServerDate, parseServerIso } from '../utils/datetime';
import { APP_CONFIG } from '../config/appConfig';
import ShopifyBillingView from '../components/billing/ShopifyBillingView';
import InvoiceLedgerSection from '../components/billing/InvoiceLedgerSection';

// Plan-ShopifyManagedPricingBilling.md (Phase 3 billing slice) — build-
// time provider switch. On the shopify build the entire Stripe billing
// surface (checkout / portal / overage / bundles / swap) is replaced by
// the Managed Pricing view, and because this is a constant baked in at
// build time the bundler tree-shakes the unused branch out of whichever
// bundle isn't using it. On the stripe build this is `false` and the
// existing page renders unchanged.
const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

// ---------- helpers ----------
const PLAN_ORDER: Plan[] = ['trial', 'starter', 'pro', 'business', 'enterprise'];
// Plan-ShopifyIsolatedFrontend.md §3.1 — filter the comparison row by
// the build's allowed plans so the Shopify bundle hides Trial / Enterprise
// (Managed Pricing catalogue is Starter/Pro/Business only). Trial isn't
// in the base list anyway — keeps existing Stripe behavior identical
// while adapting cleanly to Shopify.
const COMPARE_PLANS: Plan[] = (['starter', 'pro', 'business', 'enterprise'] as Plan[])
    .filter((p) => APP_CONFIG.allowedPlans.includes(p));

// `USAGE_METRICS[].key` (e.g. `chat_conversations`) now matches the
// `workspaces.auto_overage.<key>` storage key post-Storm `b1113a3`
// canonical-key normalization. So per-bar overage gate is a direct
// lookup — no mapping table needed.

const formatDate = (iso?: string | null) =>
  formatServerDate(iso, undefined, { month: 'short', day: 'numeric', year: 'numeric' });

// 🚨 `formatDate` returns NO_DATE ('—') for a missing/unparseable date, and
// '—' is TRUTHY. So `formatDate(x) || fallback` is DEAD CODE — the fallback
// can never fire, and the UI renders a bare em dash where a date belongs.
// This returns null instead, so `??` chains actually work. Use it anywhere a
// missing date has to change the SENTENCE rather than just the token.
const formatDateOrNull = (iso?: string | null): string | null =>
  parseServerIso(iso) ? formatDate(iso) : null;

// The one phrase every surface falls back to. Cancellation is always at
// period end, so this stays true even when we cannot name the day.
const PERIOD_END_PHRASE = 'the end of your current billing period';

const formatCurrency = (amountCents: number, currency = 'usd') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
};

const planIndex = (p: Plan) => PLAN_ORDER.indexOf(p);

// Base usage-bar primitives (usagePercent, barColor, UsageBar,
// OverrideDisclosure, overrideKeyFor/OVERRIDE_KEY_REMAP) are extracted into
// a shared module so the Shopify billing view renders identical bars without
// drift. Imported above. UsageBarWithCredits (bundle/overage segments) stays
// the Stripe-only renderer used in the Monthly Usage section below.

// ---------- confirm modal ----------
const ConfirmModal: React.FC<{
  title: string;
  onClose: () => void;
  onConfirm: () => void;
  confirming: boolean;
  confirmLabel: string;
  confirmColor?: string;
  /** Dismiss-button copy. Defaults to "Cancel" — but on the cancel-plan
   *  modal a dismiss button reading "Cancel" sits next to a confirm button
   *  reading "Cancel my plan", which is exactly backwards. Callers whose
   *  action IS a cancellation pass their own word here. */
  closeLabel?: string;
  children: React.ReactNode;
}> = ({ title, onClose, onConfirm, confirming, confirmLabel, confirmColor, closeLabel, children }) =>
  createPortal(
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="px-6 py-5 text-sm text-gray-700 dark:text-gray-300">{children}</div>
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
        <button
          onClick={onClose}
          className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm"
        >
          {closeLabel ?? 'Cancel'}
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming}
          className={`px-4 py-2 rounded-lg text-white font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 ${
            confirmColor ?? 'bg-gradient-to-r from-primary to-purple-600 hover:shadow-lg'
          }`}
        >
          {confirming && <Loader2 className="w-4 h-4 animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>,
  document.body,
);

// ---------- invoice ledger (Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08 §6) ----------
//
// Extracted to `components/billing/InvoiceLedgerSection.tsx` on 2026-08-19 so
// the Shopify billing view renders the SAME history instead of a second
// implementation that would drift. Behaviour here is unchanged: the component
// still owns `/api/billing/workspace/{id}/invoices`, still registers itself
// with the page Refresh fan-out under `invoice-ledger`, and still keeps
// loading / empty / degraded / failed as four distinct states.

// ---------- swap helpers ----------
type SwapResource = 'voice' | 'chat' | 'email';

const SWAP_LABELS: Record<SwapResource, string> = {
  voice: 'Voice min',
  chat: 'Chat convos',
  email: 'Email threads',
};

// Backend SwapRequest.direction is voice_to_chat / chat_to_voice /
// voice_to_email / email_to_voice — no chat↔email pair. One side of the
// swap must always be 'voice'. These helpers gate the dropdown options.
const validToOptions = (from: SwapResource): SwapResource[] => {
  if (from === 'voice') return ['chat', 'email'];
  return ['voice'];
};
const validFromOptions = (to: SwapResource): SwapResource[] => {
  if (to === 'voice') return ['chat', 'email'];
  return ['voice'];
};

// ---------- main page ----------
// The Stripe variant of the Billing & Usage page. On the shopify build
// this whole component is unreachable (the build-time `IS_SHOPIFY_BUILD`
// branch in `BillingPage` below returns `<ShopifyBillingView />`), so the
// bundler tree-shakes it — along with the Stripe-only child components
// (BundleCards / OverageSettingsSection / swap UI) — out of the shopify
// bundle. On the stripe build it renders exactly as before.
const StripeBillingPage: React.FC = () => {
  const { user, setUser } = useAuth();
  const { activeWorkspace, isOwner } = useWorkspace();
  const { billing, loading, error, refetch, refreshAll, refreshing } = useBilling();
  const { showToast } = useToast();
  const pollRef = useRef(0);

  // Detect session_id on mount only — once consumed, we own the state
  const [postCheckout, setPostCheckout] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return !!params.get('session_id');
  });

  const cleanSessionId = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete('session_id');
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  };

  // Poll for subscription activation after Stripe checkout return
  useEffect(() => {
    if (!postCheckout) return;
    cleanSessionId();
    pollRef.current = 0;
    const interval = setInterval(() => {
      pollRef.current += 1;
      if (pollRef.current >= 10) {
        clearInterval(interval);
        setPostCheckout(false);
        return;
      }
      refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [postCheckout]);

  // Stop polling once subscription is confirmed. StripeBillingPage only
  // mounts on the Stripe build, but gate the field lookup on
  // APP_CONFIG.billingProvider defensively so a future mis-mount on the
  // Shopify build doesn't infinitely poll for a Stripe id that will never
  // be stamped.
  const expectedSubId = IS_SHOPIFY_BUILD
    ? (billing as { shopify_subscription_id?: string | null } | null | undefined)
        ?.shopify_subscription_id
    : billing?.stripe_subscription_id;
  useEffect(() => {
    if (postCheckout && expectedSubId) {
      setPostCheckout(false);
      showToast('Subscription activated!', 'success');
    }
  }, [billing, postCheckout, expectedSubId]);

  // ─── Bundle credits (Plan-BundlesImplementation §E.1 + §E.2) ──────
  // Storm's contract makes `/api/billing/credits` a sibling endpoint of
  // `/api/billing/workspace/{id}` (separate fetch, not a sub-field on
  // the billing payload — see his §C.1 divergence note). We hold the
  // active-credit ledger in local state and pass it down to both
  // BundleCards (active-credits table) and the usage bars (credit
  // segments). Refetches: on mount, on workspace change, after the
  // bundle-checkout return-URL is detected.
  const [activeCredits, setActiveCredits] = useState<ActiveCredit[]>([]);

  const fetchCredits = async (workspaceId: string) => {
    try {
      const res = await billingAPI.listActiveCredits(workspaceId);
      setActiveCredits(res.data?.credits ?? []);
    } catch {
      // Silent fail — backend pre-Phase-2 or transient. UI degrades
      // gracefully (empty credits everywhere). No toast — user didn't
      // initiate the action.
      setActiveCredits([]);
    }
  };

  useEffect(() => {
    if (!activeWorkspace?.id) return;
    fetchCredits(activeWorkspace.id);
  }, [activeWorkspace?.id]);

  // Sibling endpoint ⇒ the Refresh button reaches it only via the registry.
  useBillingRefreshSource('active-credits', async () => {
    if (!activeWorkspace?.id) return;
    await fetchCredits(activeWorkspace.id);
  });

  // The feature flags that decide WHICH sections exist (notably
  // `billing_invoice_history`, which picks the paginated ledger over the
  // legacy table) live on the user record, and a browser reload re-reads them.
  // 🚨 Only write back when the record actually changed: `setUser` hands out a
  // new object identity, and Layout's notification SSE effect is keyed on that
  // identity — an unconditional write would tear down and re-open the
  // notification stream on every Refresh click.
  useBillingRefreshSource('auth-user', async () => {
    try {
      const res = await authAPI.getCurrentUser();
      const next = res.data;
      if (next && JSON.stringify(next) !== JSON.stringify(user)) setUser(next);
    } catch {
      // Silent: the page's own data is the point of the refresh, and a stale
      // feature flag is not worth an error toast the user cannot act on.
    }
  });

  // ─── Bundle-checkout return URL (Storm's success_url ?bundle_session=...) ──
  // Per `back/api/billing_bundles.py:create_bundle_checkout_session`, every
  // successful purchase round-trips to
  // `/billing?bundle_session={CHECKOUT_SESSION_ID}`. Detect the param on
  // mount, toast, and refetch. There is NO confirm endpoint for any bundle —
  // fulfilment is entirely webhook-driven, so this handler only observes.
  //
  // 🚨 ONE HANDLER, FOUR SKUs, AND IT CANNOT TELL THEM APART. The return URL
  // carries a Stripe session id and nothing about what was bought, so the
  // copy below has to be true of all four:
  //   - Voice / LiveAvatar / legacy $5 Text Bundle → `workspace_credits` rows,
  //     which `fetchCredits` hydrates into the Active Credits table.
  //   - Pooled Text Top-Up → NO credit row at all. `grant_text_topup` writes
  //     `workspace_text_topups` + a Redis cycle counter, and the observable
  //     change is `usage.text_tokens_topup` going non-zero, which arrives via
  //     `refetch()` and widens the pool bar's denominator.
  // Hence "allowance", not "credits": telling a top-up buyer their credits
  // were unlocked points them at a table that will never show their purchase.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('bundle_session');
    if (!sessionId) return;
    showToast('Purchase complete — your allowance has been updated.', 'success');
    // Strip the param so a page refresh doesn't re-fire the toast.
    params.delete('bundle_session');
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
    // Delay to give the webhook room to commit before we read: the user can
    // be redirected back faster than Stripe → our webhook → Mongo/Redis.
    // BOTH refetches are needed — `fetchCredits` for the three credit SKUs,
    // `refetch` for the pooled top-up. Webhook idempotency makes a
    // double-fire safe.
    if (activeWorkspace?.id) {
      setTimeout(() => {
        fetchCredits(activeWorkspace.id);
        refetch();
      }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group credits by resource for the per-bar credit-segment overlays.
  // Storm's endpoint already returns rows oldest-expiring-first, so
  // FIFO order is preserved.
  const creditsByResource = useMemo(() => {
    const map: Partial<Record<BundleResource, ActiveCredit[]>> = {};
    for (const c of activeCredits) {
      (map[c.resource] ??= []).push(c);
    }
    return map;
  }, [activeCredits]);

  // billing interval (month/year) for comparison + change requests
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(
    billing?.billing_interval ?? 'month'
  );

  // Pooled Text Top-Up, opened from the exhausted/critical state on the
  // pooled usage bar (BundleCards has its own independent entry point).
  // 🚨 Lives in StripeBillingPage only — ShopifyBillingView must never
  // reach the Stripe checkout flow.
  const [topUpOpen, setTopUpOpen] = useState(false);

  // Plan comparison grid (2026-08-17). ONE toggle for all four cards, not one
  // per card — a comparison grid whose columns are at four different heights
  // has stopped being a comparison.
  const [showAllLimits, setShowAllLimits] = useState(false);
  const [tokensExplainerOpen, setTokensExplainerOpen] = useState(false);

  // 🚨 DRIFT ALARM for the mirrored plan table. `PLAN_DISPLAY` restates
  // `back/config/plans.py::USAGE_LIMITS` because the payload only ever carries
  // the CURRENT plan's limits, so a four-plan grid cannot be data-driven
  // without a new endpoint. That mirror is only as good as the next person's
  // memory, and `main.py` re-seeds from the Python file on every boot — so an
  // edit there goes live while these cards keep quoting the old number, with
  // nothing failing. This compares the card figures against the limits the
  // server actually sent for whichever plan this workspace is on and names any
  // key that disagrees. Dev-only; one plan per workspace, but across the team's
  // workspaces that covers the tiers in practice.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const drift = checkPlanCardLimitDrift(
      billing?.plan,
      billing?.usage_limits,
      billing?.overridden_keys,
    );
    if (drift.length === 0) return;
    // eslint-disable-next-line no-console
    console.error(
      '[BillingPage] PLAN CARD DRIFT on plan "%s": %s. `PLAN_DISPLAY` in ' +
        'types/billing.ts no longer matches back/config/plans.py::USAGE_LIMITS, ' +
        'which main.py re-seeds on every boot — the cards are advertising stale ' +
        'numbers. Fix the FE table, not the payload.',
      billing?.plan,
      drift.map((d) => `${d.key} card=${d.card} server=${d.server}`).join(', '),
    );
  }, [billing?.plan, billing?.usage_limits, billing?.overridden_keys]);

  // modal state
  const [modalTarget, setModalTarget] = useState<Plan | null>(null);
  const [preview, setPreview] = useState<ProrationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Self-serve cancellation state. `cancelOpen` drives the confirm modal,
  // `cancelBusy` the in-flight button; `resuming` belongs to the inline
  // "Keep my plan" affordance on the current-plan card.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [resuming, setResuming] = useState(false);

  // Plan-InternalWorkspacePolicyV2.md §C (2026-05-15) — internal-
  // workspace detection. Under the new policy the modal-based "Managed
  // by admin" gate is gone; this flag now drives:
  //   1. Banner copy at the top (self-serve + no-billing instead of
  //      "contact your admin")
  //   2. Modal title/CTA/body branch ("Apply plan change" + no Stripe
  //      proration preview)
  //   3. Invoice History section hidden entirely (no invoices ever)
  //   4. LiveAvatarSection (delegated — see its own internal handling)
  // Optional-chain on `billing` because this hook runs before the
  // `!billing` early-return guards below.
  const isInternalWorkspace = !!billing?.is_company_owned;

  // Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08.md §6.2 — fail-closed
  // feature detect. The key is emitted by `_session_feature_flags()` only
  // when BILLING_INVOICE_HISTORY_ENABLED is on; absent (never `false`) means
  // the ledger endpoint 404s, so anything other than an explicit `true`
  // keeps today's `billing.invoices` section. This ships dark — the legacy
  // path below is what runs in production until the owner flips the flag,
  // and both paths must keep working.
  const invoiceLedgerEnabled = user?.features?.billing_invoice_history === true;

  // swap state
  const [swapFrom, setSwapFrom] = useState<SwapResource>('voice');
  const [swapTo, setSwapTo] = useState<SwapResource>('chat');
  const [swapAmount, setSwapAmount] = useState<number>(0);
  const [swapping, setSwapping] = useState(false);

  // Backend SwapRequest.direction supports only voice↔chat / voice↔email.
  // No chat↔email pair exists. So one side must always be 'voice'.
  // Auto-correct the partner dropdown when the selection drifts into an
  // invalid pair (e.g. user picks chat from voice→chat, swapTo flips to voice).
  useEffect(() => {
    const valid = validToOptions(swapFrom);
    if (!valid.includes(swapTo)) {
      setSwapTo(valid[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapFrom]);
  useEffect(() => {
    const valid = validFromOptions(swapTo);
    if (!valid.includes(swapFrom)) {
      setSwapFrom(valid[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapTo]);

  const currentIdx = billing?.plan ? planIndex(billing.plan) : -1;

  // Backend /preview-change stamps is_trial_upgrade: true when the workspace
  // has no active Stripe subscription. In that case /change-plan transparently
  // returns a checkout_url instead of mutating a subscription, so we tweak
  // the modal UX (no proration lines, "Continue to checkout" button).
  const isTrialCheckout = preview?.is_trial_upgrade === true;

  const openChangeModal = async (target: Plan) => {
    if (!activeWorkspace) return;
    setModalTarget(target);
    setPreview(null);
    setPreviewError(null);
    // Plan-InternalWorkspacePolicyV2.md §C.2 — internal workspaces skip
    // the proration preview entirely (Storm's Phase A.3 short-circuits
    // before Stripe is consulted, so the preview would 503/error). The
    // confirm-body branch for internal renders a static "this is free
    // + instant" panel that doesn't need preview data.
    if (isInternalWorkspace) return;
    setPreviewLoading(true);
    try {
      const res = await billingAPI.previewChange(activeWorkspace.id, target, billingInterval);
      setPreview(res.data);
    } catch (err: any) {
      setPreviewError(err?.response?.data?.detail || 'Could not load proration preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closeModal = () => {
    if (confirming) return;
    setModalTarget(null);
    setPreview(null);
    setPreviewError(null);
  };

  const confirmChange = async () => {
    if (!activeWorkspace || !modalTarget) return;
    setConfirming(true);
    try {
      const res = await billingAPI.changePlan(activeWorkspace.id, modalTarget, billingInterval);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      const toastMsg =
        res.data.message ||
        // Plan-InternalWorkspacePolicyV2.md §A.3 — internal short-circuit
        // returns `{status: "applied", source: "internal_workspace"}`.
        // Use a self-evident "Plan updated" toast for that path so the
        // user isn't told their plan was "scheduled" (which suggests a
        // future effective_at — there isn't one for internal).
        ((res.data as any).status === 'applied'
          ? 'Plan updated'
        : res.data.status === 'interval_switched'
          ? `Billing interval switched to ${billingInterval === 'year' ? 'yearly' : 'monthly'}`
          : res.data.status === 'scheduled'
          ? 'Plan change scheduled'
          : 'Plan change submitted');
      showToast(toastMsg, 'success');
      await refetch();
      closeModal();
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to change plan', 'error');
    } finally {
      setConfirming(false);
    }
  };

  // ─── Self-serve cancellation ────────────────────────────────────────
  //
  // Cancellation is ALWAYS at period end — the backend schedules it and the
  // workspace keeps its full limits until `effective_at`. Nothing here
  // takes effect immediately, and the modal copy says so.
  //
  // Error branches mirror the Shopify view's set (already-cancelled /
  // not-permitted / upstream failure) — toasts only, never a second modal.
  const performCancelSubscription = async () => {
    if (!activeWorkspace) return;
    setCancelBusy(true);
    try {
      const res = await billingAPI.cancelSubscription(activeWorkspace.id);
      // Prefer the SERVER's effective_at over the local cycle-end guess the
      // modal showed — the server is the one that scheduled it.
      // 🚨 `effective_at` CAN be null: Storm deliberately does not raise when
      // Stripe returns no period at cancel time, because a 500 would tell the
      // customer their cancellation failed when it had actually succeeded.
      // Never let that reach the toast as '—' or 'Invalid Date'.
      const endDate = formatDateOrNull(res.data.effective_at) ?? PERIOD_END_PHRASE;
      const parts = [
        res.data.liveavatar_cancelled
          ? `Plan cancelled. You keep everything — including AI Avatar — until ${endDate}.`
          : `Plan cancelled. You keep everything until ${endDate}.`,
      ];
      if (res.data.released_schedule) {
        parts.push('Your scheduled plan change was dropped.');
      }
      showToast(parts.join(' '), 'success');
      await refetch();
      setCancelOpen(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        // Already scheduled to cancel — not an error the user caused.
        // Refetch so the banner appears and the button disappears.
        showToast(detail || 'Your plan is already set to cancel.', 'info');
        await refetch();
        setCancelOpen(false);
      } else if (status === 403) {
        showToast(
          detail || 'Only the workspace owner can cancel the plan.',
          'error',
        );
      } else {
        showToast(
          detail ||
            'Could not cancel your plan. Please try again or contact support.',
          'error',
        );
      }
    } finally {
      setCancelBusy(false);
    }
  };

  // "Keep my plan" — undo a scheduled cancellation. Shares its copy with
  // PendingCancellationBanner so the two entry points cannot drift.
  const performResumeSubscription = async () => {
    if (!activeWorkspace) return;
    setResuming(true);
    try {
      await billingAPI.resumeSubscription(activeWorkspace.id);
      showToast('Your plan is back on. Nothing will be cancelled.', 'success');
      await refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        showToast(detail || 'This plan is not scheduled to cancel.', 'info');
        await refetch();
      } else if (status === 403) {
        showToast(
          detail || 'Only the workspace owner can change the plan.',
          'error',
        );
      } else {
        showToast(
          detail ||
            'Could not restore your plan. Please try again or contact support.',
          'error',
        );
      }
    } finally {
      setResuming(false);
    }
  };

  const handleSwap = async () => {
    if (!activeWorkspace) return;
    if (swapFrom === swapTo) {
      showToast('Pick different resources to swap', 'error');
      return;
    }
    if (swapAmount <= 0) {
      showToast('Amount must be greater than 0', 'error');
      return;
    }
    setSwapping(true);
    try {
      await billingAPI.applySwap(activeWorkspace.id, {
        direction: `${swapFrom}_to_${swapTo}` as
          | 'voice_to_chat'
          | 'chat_to_voice'
          | 'voice_to_email'
          | 'email_to_voice',
        amount: swapAmount,
      });
      await refetch();
      showToast('Swap applied for this cycle', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Swap failed', 'error');
    } finally {
      setSwapping(false);
    }
  };

  const swapPreview = useMemo(() => {
    if (!billing) return null;
    const ratio = swapFrom === 'voice' ? 7 : 1 / 7;
    const converted = Math.round(swapAmount * ratio);
    return { converted };
  }, [billing, swapFrom, swapAmount]);

  if (!activeWorkspace) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (loading && !billing) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (postCheckout && !billing && !loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Processing your subscription… This may take a moment.
          </p>
        </div>
      </div>
    );
  }

  if (error || !billing) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="card border-l-4 border-red-500">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">Billing unavailable</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {error || 'Could not load billing information for this workspace.'}
              </p>
              <button onClick={refetch} className="mt-3 btn-secondary text-sm">
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const limits = billing.usage_limits;
  const usage = billing.usage;
  // Plan-TextVoicePoolBilling-2026-08.md §3 — pooled Text+Voice pricing,
  // read from the payload (never a frontend env flag). Drives the two-bar
  // Monthly Usage shape below and hides the per-category machinery the pool
  // replaces (Resource Swap, Text Bundles, per-category auto-overage).
  // Nothing is deleted here — Phase D deletes.
  // 🚨 Takes the WHOLE billing object, not `limits`: the authority is the
  // top-level `text_pool_enabled` flag, not the presence of a limit key.
  const pooled = isPooledBilling(billing);
  // One definition of the avatar allowance, shared with LiveAvatarSection and
  // ShopifyBillingView — see `billing/liveAvatarMinutes.ts`.
  const avatarMinutes = resolveAvatarMinutes({
    serverCapTotal: billing.liveavatar_addon?.minutes_cap_total,
    addonMinutesCap: billing.liveavatar_addon?.minutes_cap,
    planLimit: limits.max_liveavatar_minutes,
    credits: creditsByResource.liveavatar_minutes ?? [],
    used: usage.liveavatar_minutes ?? 0,
  });
  // Plan-LiveAvatarInstructionalPage F3 — AI Avatar entitlement (app build:
  // add-on only; mirrors AgentEditorModal.tsx). Drives the "Unlock AI Avatar"
  // CTA below, shown only to non-entitled users.
  const canUseAvatar = billing.liveavatar_addon?.status === 'active';
  // Plan-RemoveFreeTrial.md — a brand-new / never-paid workspace has
  // `plan: null` + `subscription_status: "incomplete"` (locked). The Layout
  // allowlist lets the locked owner REACH this page to pick a plan, so we
  // must render null-plan-safe: every plan-dependent section is hidden and an
  // onboarding header replaces the Current Plan card. When a plan IS set the
  // page renders exactly as before.
  const hasPlan = !!billing.plan;
  const planDisplay = billing.plan ? PLAN_DISPLAY[billing.plan] : null;
  const targetIsUpgrade = modalTarget ? planIndex(modalTarget) > currentIdx : false;

  // ─── Plan-card limit rows ───────────────────────────────────────────
  //
  // Under pooled billing the pool-gated rows are not caps at all, so the cards
  // must not advertise them. `isPooledBilling` is the one derivation — not a
  // second copy of the flag.
  const availableLimitRows = PLAN_CARD_LIMITS.filter(
    (r) => !r.poolGated || !pooled,
  );
  // 🚨 The toggle is rendered only when something is actually behind it. Under
  // pooled billing every remaining row is `primary`, so the control would
  // expand to reveal nothing — a disclosure that discloses nothing is worse
  // than no disclosure, and this button is what the owner pointed at.
  const hasExpanderRows = availableLimitRows.some((r) => !r.primary);
  const visibleLimitRows = availableLimitRows.filter(
    (r) => r.primary || showAllLimits,
  );

  // ─── Cancellation gating ────────────────────────────────────────────
  //
  // Absent `pending_cancellation` means "nothing scheduled", not "unknown" —
  // a backend predating the field simply never schedules one.
  const pendingCancellation = billing.pending_cancellation ?? null;
  // A Stripe subscription is the thing being cancelled, so its id — not the
  // plan name — is the gate. This also excludes internal / company-owned
  // workspaces, which have no Stripe subscription at all, and never-paid
  // workspaces sitting at `incomplete`.
  //
  // 🚨 `!isInternalWorkspace` is load-bearing, not defensive. Storm's prod
  // census: of the 8 workspaces holding a Stripe subscription id, SIX are
  // `is_company_owned`. The cancel endpoint refuses those with a 400 by
  // design (`_block_if_internal`), so without this the most-likely workspace
  // anyone tests on prod shows a cancel button that errors.
  //
  // We HIDE rather than route to a modal, and that is the house pattern here
  // even though it looks like the exception. Upgrade/downgrade are not gated
  // for internal workspaces — they render and WORK, applying instantly with
  // no Stripe (`confirmChange` → `{status: 'applied'}`), which is why the
  // plan-change modal only swaps its copy. There is no such thing as
  // cancelling a plan that was never billed, so the analogous action does not
  // exist rather than behaving differently. The blue "Internal workspace"
  // banner at the top of this page already explains the state, so nothing is
  // left unexplained by the button's absence.
  //
  // (`InternalWorkspaceModal.tsx` still exists and its header claims it
  // "replaces every plan-change CTA" — that comment is STALE. The modal has
  // had no call sites since Plan-InternalWorkspacePolicyV2 §C.2 removed the
  // gate on 2026-05-15. Do not wire new flows into it.)
  const canCancelSubscription =
    hasPlan &&
    !isInternalWorkspace &&
    !!billing.stripe_subscription_id &&
    ['active', 'trialing', 'past_due'].includes(billing.subscription_status);
  // Hidden once a cancellation is pending — the banner and the inline
  // "Keep my plan" line take over from there.
  const showCancelButton = canCancelSubscription && !pendingCancellation;
  // The modal has no server answer yet, so it shows the local cycle end.
  // The success toast afterwards uses the server's `effective_at`.
  //
  // Caveat worth knowing, deliberately NOT re-plumbed: `billing_cycle_end` is
  // a stored mirror while the toast's `effective_at` is read live from Stripe,
  // so a missed webhook can make this dialog show a stale date and the toast
  // the true one. It self-heals at each renewal.
  //
  // 🚨 `??`, not `||` — see `formatDateOrNull`. With `||` this whole fallback
  // chain was dead code, because '—' is truthy.
  const cancelEffectiveDate =
    formatDateOrNull(billing.billing_cycle_end) ??
    formatDateOrNull(billing.trial_ends_at);
  const cancelEffectiveLabel = cancelEffectiveDate ?? PERIOD_END_PHRASE;
  // 🚨 The avatar warning is gated on the BACKEND's flag and nothing else.
  // NOT `canUseAvatar` (`liveavatar_addon.status === 'active'`) — that is
  // `true` for a COUPON-granted add-on, which has no Stripe subscription item
  // behind it and therefore SURVIVES a plan cancellation. Gating on it would
  // tell those customers their avatar is being cancelled when it is not, and
  // a false warning in a destructive dialog is worse than no warning.
  // Absent → `false`: a missing warning is the safe failure, never a false one.
  const avatarWillCancel = billing.liveavatar_will_cancel_on_plan_cancel === true;

  // Two independent scheduled changes are destroyed by a cancellation.
  const hasScheduledChange =
    !!billing.pending_plan_change || !!billing.liveavatar_addon?.pending_change;
  // Name the change we are about to destroy — "your scheduled change" is
  // vague at exactly the moment the customer needs to weigh what they lose.
  const scheduledChangeParts = (() => {
    const parts: string[] = [];
    if (billing.pending_plan_change) {
      const name =
        PLAN_DISPLAY[billing.pending_plan_change.target_plan]?.name ??
        billing.pending_plan_change.target_plan;
      const interval =
        billing.pending_plan_change.target_billing_interval === 'year'
          ? ' (yearly)'
          : billing.pending_plan_change.target_billing_interval === 'month'
            ? ' (monthly)'
            : '';
      parts.push(`your plan change to ${name}${interval}`);
    }
    const av = billing.liveavatar_addon?.pending_change;
    if (av) {
      parts.push(
        av.target_tier
          ? `your AI Avatar change to ${av.target_tier.charAt(0).toUpperCase()}${av.target_tier.slice(1)}`
          : 'your scheduled AI Avatar cancellation',
      );
    }
    return parts;
  })();
  // A workspace can have BOTH a plan change and an avatar change scheduled,
  // so the block has to read correctly in the plural too.
  const scheduledChangeText = scheduledChangeParts.join(' and ');
  const scheduledChangePlural = scheduledChangeParts.length > 1;

  return (
    <div className="max-w-6xl mx-auto pb-10 space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl font-bold gradient-text">Billing & Usage</h1>
        {/* 🚨 `refreshAll`, NOT `refetch`. Half this page is fetched by the
            components that render it (invoice ledger, active credits, top-up
            history), and a Refresh that only refetched the main payload left a
            freshly-paid invoice invisible until the browser was reloaded.
            The busy state matters as much as the fan-out: the button used to
            fire silently, which is why a refresh that did nothing looked like
            a refresh that had worked. */}
        <button
          onClick={refreshAll}
          disabled={refreshing}
          aria-busy={refreshing}
          className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary flex items-center gap-2 disabled:opacity-70 disabled:cursor-wait disabled:hover:text-gray-600 dark:disabled:hover:text-gray-400"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {/* The busy word only appears from `sm` up. At 375px "Refreshing…"
              is wide enough to push the button off the title's line, so the
              header would jump to two rows for the duration of every refresh —
              the spinner and the disabled state carry the state on mobile.
              Same long/short label idiom as InvoiceActionLink above. */}
          {refreshing ? (
            <>
              <span className="hidden sm:inline">Refreshing…</span>
              <span className="sm:hidden">Refresh</span>
            </>
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      {postCheckout && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-4 py-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing your subscription… This may take a moment.
        </div>
      )}

      {/* Plan-InternalWorkspacePolicyV2.md §C.2 (2026-05-15) — internal-
          workspace banner. Supersedes the Plan-RemoveStripeSandbox §3.1
          "managed by admin" banner: under the new policy, internal
          workspaces self-serve plan + LA changes (no Stripe round-trip)
          and DO enforce plan limits the same as paying clients. The
          banner now communicates the self-serve + no-billing reality
          instead of asking the user to contact an admin. */}
      {billing.is_company_owned && (
        <div className="border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-xl px-4 sm:px-5 py-3 sm:py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4 text-blue-700 dark:text-blue-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                Internal workspace
              </p>
              <p className="text-xs italic text-blue-800 dark:text-blue-300 mt-0.5 leading-snug">
                Plan changes apply immediately without billing. Usage is
                tracked and enforced against your current plan limits the
                same as paying customers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 1. Current Plan card — OR onboarding header when no plan is set.
          A never-paid workspace (`plan: null`, status `incomplete`) gets an
          onboarding card matching SubscriptionRequiredGate's copy/tone, and
          the plan-dependent sections below are all hidden. */}
      {!hasPlan ? (
        <section className="card">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Choose a plan to get started
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                Pick a plan below to activate your workspace and bring your
                agents online.
              </p>
            </div>
          </div>
        </section>
      ) : (
      <section className="card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {planDisplay?.name}
                </h2>
                <span
                  className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                    billing.subscription_status === 'active'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : billing.subscription_status === 'trialing'
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                      : billing.subscription_status === 'past_due'
                      ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {billing.subscription_status}
                </span>
              </div>
              {/* Shared with the Shopify card so the interval treatment
                  cannot drift between the two surfaces again. The pill is no
                  longer gated on `priceNum` — Enterprise has no numeric price
                  and used to show no interval at all. */}
              <PlanPriceLine
                planDisplay={planDisplay}
                billingInterval={billing.billing_interval}
              />
              {billing.subscription_status === 'trialing' && billing.trial_ends_at && (
                <p className="text-sm text-indigo-600 dark:text-indigo-400 mt-2 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" />
                  Trial ends {formatDate(billing.trial_ends_at)}
                </p>
              )}
              {billing.billing_cycle_end && billing.subscription_status !== 'trialing' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Next renewal: <span className="font-semibold">{formatDate(billing.billing_cycle_end)}</span>
                </p>
              )}
              {/* Authoritative usage-limit reset date (Storm 4e5c20d).
                  Suppressed on trial — the "Trial ends {date}" line above
                  already conveys the same date; mirror the "Next renewal"
                  gate (non-trialing + value present). */}
              {billing.usage_resets_at && billing.subscription_status !== 'trialing' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Usage limits reset on <span className="font-semibold">{formatDate(billing.usage_resets_at)}</span>
                </p>
              )}
              {billing.pending_plan_change && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" />
                  Switching to {PLAN_DISPLAY[billing.pending_plan_change.target_plan].name}
                  {billing.pending_plan_change.target_billing_interval
                    ? ` · ${billing.pending_plan_change.target_billing_interval === 'year' ? 'Yearly' : 'Monthly'}`
                    : ''}{' '}
                  on {formatDate(billing.pending_plan_change.effective_at)}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Plan-dependent sections (Usage, Account resources, Resource swap,
          Bundle purchase, Auto-overage, LiveAvatar) are meaningless for a
          never-paid workspace — hide them all until a plan is chosen. The
          Plan comparison grid + Invoice history stay visible below. */}
      {hasPlan && (
      <>
      {/* 2. Usage bars — Plan-BundlesImplementation §E.2.
          Multi-segment bars: base allowance + per-credit-row stacked
          segments (FIFO, oldest-expiring first) + amber overage tail
          when auto_overage is enabled for the resource. */}
      <section className="card">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">Monthly Usage</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {/* Pooled mode (Plan-TextVoicePoolBilling-2026-08 §3): TWO primary
              bars — AI text tokens + voice minutes — in place of the six
              per-category bars. The per-category counts survive as the
              informational breakdown inside TextPoolBar; they are counts, not
              caps, once the pool is the only text limit. The legacy branch is
              byte-identical to what shipped before, so a flag-off payload
              renders exactly today's page. */}
          {pooled ? (
            <>
              <TextPoolBar
                usage={usage}
                limit={limits.max_text_tokens}
                overriddenKeys={billing.overridden_keys}
                // Owner-gated to match BundleCards — a non-owner cannot
                // purchase, so offering them the button would dead-end.
                // Trial workspaces likewise buy nothing (§E.4).
                onTopUp={
                  isOwner && billing.subscription_status !== 'trialing'
                    ? () => setTopUpOpen(true)
                    : undefined
                }
              />
              {/* Voice keeps the credit-aware renderer: minutes are still
                  metered in minutes and Voice Bundles still credit them, so
                  the credit / overage segments stay meaningful in pooled
                  mode. Only the TEXT side pools. */}
              <div key="voice_minutes">
                <UsageBarWithCredits
                  label="Voice Minutes"
                  used={usage.voice_minutes ?? 0}
                  baseLimit={limits.max_voice_minutes}
                  credits={creditsByResource.voice_minutes ?? []}
                  unit="min"
                  autoOverageEnabled={billing.auto_overage?.voice_minutes === true}
                  resourceKey="voice_minutes"
                />
                <OverrideDisclosure
                  resourceKey="voice_minutes"
                  overriddenKeys={billing.overridden_keys}
                />
              </div>
            </>
          ) : (
            USAGE_METRICS.map((m) => {
              // `m.key` is canonical (`chat_conversations`, …) — matches
              // both `workspaces.auto_overage.<key>` (post-`b1113a3`)
              // and `BundleResource`. Direct lookup, no translation.
              const autoOverageEnabled = billing.auto_overage?.[m.key] === true;
              const resourceCredits =
                (creditsByResource[m.key as BundleResource] ?? []) as ActiveCredit[];
              return (
                <div key={m.key}>
                  <UsageBarWithCredits
                    label={m.label}
                    used={(usage[m.key] as number) ?? 0}
                    baseLimit={limits[m.limitKey] as number}
                    credits={resourceCredits}
                    unit={m.unit}
                    autoOverageEnabled={autoOverageEnabled}
                    resourceKey={m.key as BundleResource}
                  />
                  <OverrideDisclosure
                    resourceKey={overrideKeyFor(m.key as string)}
                    overriddenKeys={billing.overridden_keys}
                  />
                </div>
              );
            })
          )}

          {/* LiveAvatar minutes — new bar per §E.2.
              No auto-overage (LiveAvatar hard-blocks per §A.7).
              Hidden when the plan has no LiveAvatar quota (max=0) and
              no credits — keeps the section clean for plans that
              don't include LiveAvatar at all.

              🚨 `baseLimit` is the ADD-ON TIER's minutes, NOT
              `max_liveavatar_minutes`. `back a04e767` moved the avatar base
              allowance onto the effective tier cap and off the plan limit,
              which is 0 on every App plan (only Shopify Business sets 60).
              Passing the plan limit divided a 50-minute bundle by 50 while
              ignoring the 60 tier minutes entirely — "72 / 50", a second
              overflow with a different cause than the one on the add-on card.
              `resolveAvatarMinutes` owns that choice for all three surfaces;
              `UsageBarWithCredits` then re-derives the same sum internally
              (base + Σ`amount_initial`), so the two agree by construction. */}
          {(avatarMinutes.tierCap !== 0 ||
            (creditsByResource.liveavatar_minutes ?? []).length > 0) && (
            <div key="liveavatar_minutes">
              {/* 🚨 When the server sends `minutes_cap_total`, the credit rows
                  are withheld from this bar ON PURPOSE. `UsageBarWithCredits`
                  derives its own denominator as `baseLimit + Σamount_initial`,
                  and `back bc75d88` states that sum is WRONG — it counts
                  bundles fully drained in EARLIER cycles, inflating a
                  denominator whose numerator resets monthly. Feeding it the
                  server total as the base with an empty credit list makes the
                  component's arithmetic the identity, so the number it prints
                  is the server's. The cost is the per-credit segment shading on
                  this one bar; a correct denominator beats decorative
                  segmentation built on a formula the backend just retired. The
                  fallback path (old backend, no field) keeps the segments. */}
              <UsageBarWithCredits
                label="LiveAvatar Minutes"
                used={usage.liveavatar_minutes ?? 0}
                baseLimit={
                  avatarMinutes.unlimited
                    ? -1
                    : typeof billing.liveavatar_addon?.minutes_cap_total === 'number'
                    ? avatarMinutes.displayCap
                    : avatarMinutes.tierCap
                }
                credits={
                  typeof billing.liveavatar_addon?.minutes_cap_total === 'number'
                    ? []
                    : creditsByResource.liveavatar_minutes ?? []
                }
                unit="min"
                autoOverageEnabled={false}
                resourceKey="liveavatar_minutes"
              />
              <OverrideDisclosure
                resourceKey="liveavatar_minutes"
                overriddenKeys={billing.overridden_keys}
              />
            </div>
          )}

          {/* Plan-InternalWorkspacePolicyV2.md §B.4 — Video analyses
              monthly bar. No bundle credits (not in BundleResource
              union), so this uses the simple `UsageBar` instead of
              `UsageBarWithCredits`. Hidden when the plan has no
              video-analysis quota (older plans pre-§B.5), and in POOLED mode,
              where `max_video_analyses` is retired — video analyses draw
              text-pool tokens like every other text surface and appear in the
              TextPoolBar breakdown instead of carrying their own cap. */}
          {!pooled && (limits.max_video_analyses ?? 0) !== 0 && (
            <div key="video_analyses">
              <UsageBar
                label="Video Analyses"
                used={usage.video_analyses ?? 0}
                limit={limits.max_video_analyses ?? 0}
                unit="videos"
              />
              <OverrideDisclosure
                resourceKey="video_analyses"
                overriddenKeys={billing.overridden_keys}
              />
            </div>
          )}

          {/* Plan-StylistUsageMetering.md (2026-06-29) — Stylist results.
              NOT plan-gated (informational count, not a capped limit). Stylist
              is a Shopify-only feature, so on this Stripe view it is gated on
              DATA PRESENCE (>0) — not plan tier — to avoid a dead 0-row for
              Stripe-only customers who can't use Stylist, while still
              surfacing it for a Stripe workspace that also has a Shopify store.
              ShopifyBillingView shows it un-gated for every tier. No cap →
              `limit={-1}` renders `used / ∞`. Billing rides chat_conversations.
              Pooled mode: this count moves into the TextPoolBar breakdown. */}
          {!pooled && (usage.stylist_results ?? 0) > 0 && (
            <div key="stylist_results">
              <UsageBar
                label="Stylist Results"
                used={usage.stylist_results ?? 0}
                limit={-1}
                unit="results"
              />
              <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mt-0.5">
                Each Stylist result uses 2 chat-conversation credits.
              </p>
            </div>
          )}

          {/* Orchestrator messages — token-metered, charged to the chat
              allowance (informational count, not a capped limit). UN-gated:
              renders for every workspace, including the 0-usage state
              (`limit={-1}` → `used / ∞`, so zero reads as `0 / ∞`).
              DO NOT copy the Stylist row's `>0` gate here — the two rows
              differ deliberately. Stylist is a Shopify-only feature, so on
              this Stripe view its `>0` gate suppresses a permanently-dead row
              for customers who cannot use it. The Orchestrator ships on BOTH
              builds, so hiding it until first use would hide an available
              feature from the customers meant to discover it. Matches
              ShopifyBillingView, which renders this row un-gated too.
              🚨 Pooled mode HIDES this row: the italic copy below ("metered
              against your chat allowance") is false once the Orchestrator
              debits `text_tokens` directly and skips the chat-credit branch
              (plan §A3). The message count reappears in the TextPoolBar
              breakdown, where no allowance is claimed for it. */}
          {!pooled && (
          <div key="orchestrator_messages">
            <UsageBar
              label="Orchestrator Messages"
              used={usage.orchestrator_messages ?? 0}
              limit={-1}
              unit="messages"
            />
            <p className="text-[11px] italic text-gray-500 dark:text-gray-400 mt-0.5">
              {/* 🚨 Copy must be true in BOTH modes. With
                  `ORCHESTRATOR_BILLING_ENABLED` off, `orchestrator_credits`
                  accumulates but `chat_conversations` is never debited — so
                  "charged … used" contradicted the Chat Conversations bar in
                  this same card, which excludes them. "Metered against" is
                  accurate whether or not the debit is switched on. Duplicated
                  VERBATIM in ShopifyBillingView.tsx — the two must move
                  together. */}
              Orchestrator activity is metered against your chat allowance — {usage.orchestrator_credits ?? 0} chat credits so far.
            </p>
          </div>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          {/* 🚨 The legacy line is only true while the per-category counters
              are count-only. In pooled mode the text pool DOES block (widget
              402 / email skip per plan §A4), so the footnote must say so —
              duplicated verbatim in ShopifyBillingView.tsx. */}
          {pooled
            ? 'Your AI text and voice allowances are enforced — when a pool runs out, that channel pauses until your limits reset.'
            : 'Non-voice limits are tracked silently during Phase A — usage never blocks functionality. Voice enforcement is active.'}
        </p>

        {/* Plan-LiveAvatarInstructionalPage F3 — discoverability CTA for the
            AI Avatar add-on, shown only when NOT entitled. When entitled, the
            LiveAvatar usage row above already shows tracking. Scrolls to the
            existing LiveAvatarSection (id="liveavatar") on this page. */}
        {!canUseAvatar && (
          <a
            href="#liveavatar"
            className="mt-4 flex items-center gap-3 p-3 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
          >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-purple-600 text-white shrink-0">
              <Bot className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Add a lifelike AI Avatar to your voice agent
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Give your agent a face — get the AI Avatar add-on.
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-primary shrink-0" />
          </a>
        )}
      </section>

      {/* 2b. Account resources */}
      <section className="card">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-5">Account Resources</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <UsageBar label="Agents" used={usage.agents ?? 0} limit={limits.max_agents} unit="" />
            <OverrideDisclosure
              resourceKey="agents"
              overriddenKeys={billing.overridden_keys}
            />
          </div>
          <div>
            <UsageBar label="Articles" used={usage.articles ?? 0} limit={limits.max_articles} unit="" />
            <OverrideDisclosure
              resourceKey="articles"
              overriddenKeys={billing.overridden_keys}
            />
          </div>
          <div>
            <UsageBar label="Team Members" used={usage.team_members ?? 0} limit={limits.max_team_members} unit="" />
            <OverrideDisclosure
              resourceKey="team_members"
              overriddenKeys={billing.overridden_keys}
            />
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          These are account-wide limits, not monthly.
        </p>
      </section>
      </>
      )}

      {/* 3. Plan comparison — ALWAYS visible. This is how a locked workspace
          picks + pays. `currentIdx === -1` and `p === billing.plan` is false
          for every row when no plan is set, so all CTAs read "Upgrade". */}
      <section className="card">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Plans</h2>
          <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1 relative">
            <button
              onClick={() => setBillingInterval('month')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                billingInterval === 'month'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('year')}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                billingInterval === 'year'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              Yearly
              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                Save 17%
              </span>
            </button>
          </div>
        </div>
        {billingInterval === 'year' && (
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-4 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Yearly billing gives you 2 months free.
          </p>
        )}

        {/* "What are AI text tokens?" — stated ONCE above the grid rather than
            repeated on four cards, and placed above them so it is read before
            the numbers it explains.

            🚨 OWNER DECISION (2026-08-17): TOKENS ONLY — NO CONVERSATION
            ESTIMATE, not even a range. A "≈N conversations" figure was proposed
            and rejected, and the measured data is why: the mean is ~70k
            effective tokens per conversation, but the median is under half of
            that and the largest single observed conversation was 4.04M — one
            conversation eating two thirds of a Starter plan. Any point estimate
            is wrong for most customers in BOTH directions, and on a pricing page
            a wrong estimate is a promise. The copy below therefore says outright
            that there is no fixed number of chats in an allowance, so a reader
            cannot infer the number we declined to print. Do not add one. */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setTokensExplainerOpen((v) => !v)}
            aria-expanded={tokensExplainerOpen}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
          >
            <HelpCircle className="w-3.5 h-3.5 flex-shrink-0" />
            What are AI text tokens?
            <ChevronDown
              className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${
                tokensExplainerOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {tokensExplainerOpen && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl">
              Tokens measure the AI writing and reading your assistant does —
              chat replies, email replies, and processing the content in your
              Knowledge Hub. Longer, more complex conversations use more and
              short ones use very little, so there is no fixed number of chats
              in an allowance. Your plan&apos;s tokens reset at the start of
              each billing cycle. Top-up tokens you buy never expire, and are
              only drawn on once the plan allowance runs out.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {COMPARE_PLANS.map((p) => {
            const disp = PLAN_DISPLAY[p];
            const isCurrent =
              p === billing.plan &&
              (billing.billing_interval ?? 'month') === billingInterval;
            const isUpgrade = planIndex(p) > currentIdx;
            const isEnterprise = p === 'enterprise';
            const ctaLabel = isCurrent
              ? 'Current plan'
              : isEnterprise
              ? 'Contact sales'
              : isUpgrade
              ? 'Upgrade'
              : 'Downgrade';
            const ctaDisabled = isCurrent || (!isOwner && !isEnterprise);
            const showYearly = billingInterval === 'year' && !isEnterprise;
            const priceMain = showYearly ? disp.yearlyPrice : disp.price;
            const priceSuffix = disp.priceNum !== null ? (showYearly ? '/yr' : '/mo') : '';
            return (
              <div
                key={p}
                className={`relative p-5 rounded-xl border-2 transition-all ${
                  isCurrent
                    ? 'border-primary bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'
                }`}
              >
                {isCurrent && (
                  <span className="absolute -top-2.5 right-4 bg-primary text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
                <h3 className="font-bold text-gray-900 dark:text-white">{disp.name}</h3>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  {priceMain}
                  {priceSuffix && <span className="text-sm text-gray-500">{priceSuffix}</span>}
                </p>
                {showYearly && disp.yearlyPriceNum !== null && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {disp.yearlyMonthlyEquivalent}/mo billed annually
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{disp.tagline}</p>

                {/* 🚨 THE HEADLINE IS THE AI TEXT ALLOWANCE. Until 2026-08-17
                    these cards showed the agent cap and nothing else — the one
                    thing the customer is actually buying was absent from the
                    page that sells it. Agents moved down into the limit list
                    where it belongs, beside the other real caps. */}
                <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 px-3 py-2">
                  <p className="text-base font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
                    {disp.textTokens < 0
                      ? 'Unlimited'
                      : `${formatTokenCount(disp.textTokens)} tokens`}
                  </p>
                  <p className="text-[11px] text-gray-600 dark:text-gray-400 leading-snug">
                    AI text per month
                  </p>
                </div>

                {/* The genuine caps. 🚨 No chat-conversation or email-thread
                    row: under pooled billing those are informational counts
                    drawn from the allowance above, not limits — see
                    PLAN_CARD_LIMITS. `showAllLimits` is ONE toggle for the
                    whole grid rather than one per card, so expanding keeps the
                    four cards row-aligned and comparable; per-card expanders
                    would leave a comparison grid at four different heights. */}
                <dl className="mt-3 mb-4 space-y-1">
                  {visibleLimitRows.map((r) => (
                    <div key={r.key} className="flex items-baseline justify-between gap-2">
                      <dt className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {r.label}
                      </dt>
                      <dd className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 tabular-nums flex-shrink-0">
                        {formatPlanLimit(disp.limits[r.key])}
                      </dd>
                    </div>
                  ))}
                </dl>
                {/* Plan-InternalWorkspacePolicyV2.md §C.2 (2026-05-15) —
                    internal-workspace plan CTA. The old "Managed by
                    admin" short-circuit is gone: internal workspaces
                    now self-serve plan changes (and the modal/POST
                    flow detects internal via `is_company_owned` and
                    skips Stripe — see `confirmChange()` below + Storm's
                    Phase A.3 short-circuit returning
                    `{status: "applied", ...}`). Enterprise tier still
                    routes through the in-widget upgrade chat for both
                    internal and external workspaces. */}
                {isEnterprise && !isCurrent ? (
                  // Plan-EnterpriseUpsellViaWidget §A.2 — Enterprise CTA
                  // opens the Prodagi Help Widget and auto-sends the
                  // upgrade prompt. Util falls back to mailto when the
                  // widget isn't mounted (dev / pre-deploy). Internal
                  // workspaces already short-circuit above and never
                  // reach this branch.
                  <button
                    type="button"
                    onClick={openEnterpriseUpgradeChat}
                    className="w-full block text-center py-2 rounded-lg text-sm font-semibold transition-all bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg"
                  >
                    {ctaLabel}
                  </button>
                ) : (
                  <button
                    onClick={() => openChangeModal(p)}
                    disabled={ctaDisabled}
                    className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                      isCurrent
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 cursor-not-allowed'
                        : isUpgrade
                        ? 'bg-gradient-to-r from-primary to-purple-600 text-white hover:shadow-lg'
                        : 'border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={!isOwner && !isCurrent ? 'Only workspace owners can change plans' : ''}
                  >
                    {ctaLabel}
                  </button>
                )}

                {/* Cancel / undo — lives on the CURRENT plan's card only,
                    directly under its disabled "Current plan" CTA.
                    🚨 ONE cancel affordance on the page: this was relocated
                    from the plan-summary section, not duplicated.
                    Cancel and its undo deliberately sit in the SAME slot —
                    splitting them across two sections is how someone loses
                    the way back. The Layout banner still carries the
                    always-visible path, but it is dismissible, which is why
                    the in-page undo has to survive dismissal.
                    All gating is `showCancelButton` exactly as derived above
                    (plan + not internal + a real Stripe subscription +
                    cancellable status + nothing already pending) — not
                    re-derived here. */}
                {isCurrent && showCancelButton && (
                  <button
                    onClick={() => setCancelOpen(true)}
                    disabled={!isOwner || cancelBusy}
                    title={
                      !isOwner ? 'Only workspace owners can cancel the plan' : ''
                    }
                    // Secondary destructive style, shared with the Shopify
                    // view so the two builds cannot drift. Intentionally NOT a
                    // solid red CTA: cancellation is a deliberate, recoverable
                    // action (the customer keeps access to period end and can
                    // resume), so a softer signal is the right altitude.
                    // 🚨 Do not "improve" this into a solid red button.
                    // `w-full`, not the Shopify `flex-1`: that one sits BESIDE
                    // its CTA in a flex row, this one stacks below it.
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all border-2 border-red-300 dark:border-red-700/60 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {cancelBusy && (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    )}
                    Cancel subscription
                  </button>
                )}

                {isCurrent && pendingCancellation && (
                  <div className="mt-2">
                    <p className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1.5">
                      <XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>
                        {/* 🚨 `effective_at` can be null — render a sentence
                            that still reads, never a bare '—'. */}
                        {formatDateOrNull(pendingCancellation.effective_at) ? (
                          <>
                            Your plan ends on{' '}
                            <span className="font-semibold">
                              {formatDate(pendingCancellation.effective_at)}
                            </span>
                          </>
                        ) : (
                          <>Your plan ends at {PERIOD_END_PHRASE}</>
                        )}
                        . You keep your full limits until then.
                        {pendingCancellation.liveavatar_will_cancel
                          ? ' Your AI Avatar add-on ends on the same day.'
                          : ''}
                      </span>
                    </p>
                    <button
                      onClick={performResumeSubscription}
                      disabled={!isOwner || resuming}
                      title={
                        !isOwner
                          ? 'Only workspace owners can change the plan'
                          : ''
                      }
                      // Matches the cancel button's weight on purpose — this
                      // is the MORE important of the two controls, because it
                      // is the way back, and a text link beside a bordered
                      // button reads as an afterthought. Primary colour, not
                      // red: restoring a plan is not a destructive action.
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all border-2 border-primary/40 dark:border-primary/50 text-primary hover:bg-primary/5 dark:hover:bg-primary/10 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {resuming && (
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                      )}
                      Keep my plan
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ONE toggle for the whole grid — see the note on the `<dl>` above.
            Full-width tap target at 375px, auto from `sm` up.
            Rendered ONLY when there is something behind it: under pooled
            billing every surviving row is on the face already. */}
        {hasExpanderRows && (
        <button
          type="button"
          onClick={() => setShowAllLimits((v) => !v)}
          aria-expanded={showAllLimits}
          className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          {showAllLimits ? 'Show fewer limits' : 'Compare all limits'}
          <ChevronDown
            className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${
              showAllLimits ? 'rotate-180' : ''
            }`}
          />
        </button>
        )}
      </section>

      {/* Sections 4 / 4b / 5 are plan-dependent (swap allowance, bundle
          purchase, auto-overage) — hidden for a never-paid workspace. */}
      {hasPlan && (
      <>
      {/* 4. Resource swap — HIDDEN in pooled mode (not deleted; Phase D
          deletes it, along with its compounding-swap bug). There is nothing
          left to swap between once chat/email/knowledge/scrapes/reports are
          one pool, and the swap endpoint mutates retired per-category keys. */}
      {!pooled && (
      <section className="card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Resource Swap</h2>
          <span className="text-xs text-gray-500 dark:text-gray-400">1 voice min = 7 conversations · 50% max</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Shift allowance between resources for this billing cycle.
        </p>
        <div className="grid sm:grid-cols-[1fr_auto_1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">From</label>
            <select
              value={swapFrom}
              onChange={(e) => setSwapFrom(e.target.value as SwapResource)}
              className="input-field"
              disabled={!isOwner}
            >
              {(['voice', 'chat', 'email'] as SwapResource[]).map((opt) => (
                <option key={opt} value={opt}>{SWAP_LABELS[opt]}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:flex items-center justify-center h-10 mb-0.5">
            <ArrowRight className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">To</label>
            <select
              value={swapTo}
              onChange={(e) => setSwapTo(e.target.value as SwapResource)}
              className="input-field"
              disabled={!isOwner}
            >
              {validToOptions(swapFrom).map((opt) => (
                <option key={opt} value={opt}>{SWAP_LABELS[opt]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Amount</label>
            <input
              type="number"
              min={0}
              value={swapAmount}
              onChange={(e) => setSwapAmount(Number(e.target.value))}
              className="input-field"
              disabled={!isOwner}
            />
          </div>
          <button
            onClick={handleSwap}
            disabled={!isOwner || swapping || swapAmount <= 0 || swapFrom === swapTo}
            className="btn-primary h-10 whitespace-nowrap disabled:opacity-50"
          >
            {swapping ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply swap'}
          </button>
        </div>
        {swapAmount > 0 && swapFrom !== swapTo && swapPreview && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
            Preview: <span className="font-semibold">{swapAmount}</span> {swapFrom} →{' '}
            <span className="font-semibold">{swapPreview.converted}</span> {swapTo}
          </p>
        )}
        {!isOwner && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">Read-only — only workspace owners can swap resources.</p>
        )}
      </section>
      )}

      {/* 4b. Bundle purchase + active credits (Plan-BundlesImplementation §E.1 + §E.4) */}
      <BundleCards
        billing={billing}
        workspaceId={activeWorkspace.id}
        isOwner={isOwner}
        credits={activeCredits}
      />

      {/* 4c. Top-up purchase history + standing balance. Pooled mode only:
          in legacy mode there are no top-ups to show, and the $5 Text Bundle's
          credits already appear in the Active Credits table above. Renders
          nothing at all when the workspace has never bought one (or while the
          backend flag is dark and the endpoint 404s), so it costs an empty
          workspace no vertical space.

          🚨 ORDER IS LOAD-BEARING — this used to sit ABOVE BundleCards, which
          put the standing balance and the "No active credits. Buy a bundle
          above…" empty state a full section apart, in that reading order. The
          owner read the balance, then the denial, and concluded the top-up they
          had just paid for had failed. Directly below BundleCards, the
          Active-credits panel's top-up callout and this section's headline
          balance are adjacent and obviously the same number. Proximity is half
          the fix; the conditional empty state in BundleCards is the other half.
          Do not move this back above. */}
      {pooled && <TextTopUpHistory workspaceId={activeWorkspace.id} />}

      {/* Text Top-Up modal, driven by the pooled usage bar's exhausted /
          critical CTA. BundleCards owns its own modal instance for the
          card-grid entry point; only one can be open at a time. */}
      {topUpOpen && (
        <SimpleBundleModal
          kind="text_topup"
          workspaceId={activeWorkspace.id}
          onClose={() => setTopUpOpen(false)}
        />
      )}

      {/* 5. Auto-overage toggles (Plan-BundlesImplementation §E.3) */}
      <OverageSettingsSection
        billing={billing}
        workspaceId={activeWorkspace.id}
        isOwner={isOwner}
      />
      </>
      )}

      {/* 6. Invoice history.
          Plan-InternalWorkspacePolicyV2.md §C.2 — internal workspaces
          have no Stripe invoices, ever, so we hide the section entirely
          (instead of rendering an empty "No invoices yet." panel which
          reads as an error to the user).

          Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08.md §6 — when the
          `billing_invoice_history` flag is on, the paginated ledger section
          replaces the block below. The legacy markup stays verbatim as the
          flag-off path; do not merge the two. */}
      {!isInternalWorkspace && invoiceLedgerEnabled && (
        <InvoiceLedgerSection workspaceId={activeWorkspace.id} />
      )}

      {!isInternalWorkspace && !invoiceLedgerEnabled && (
      <section className="card">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Invoice History</h2>
        {!billing.invoices || billing.invoices.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
            No invoices yet.
          </p>
        ) : (
          <div className="overflow-x-auto -mx-5 sm:mx-0">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 px-3">Date</th>
                  <th className="py-2 px-3">Invoice #</th>
                  <th className="py-2 px-3">Amount</th>
                  <th className="py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 px-3 text-gray-700 dark:text-gray-300">{formatDate(inv.created)}</td>
                    <td className="py-3 px-3 text-gray-700 dark:text-gray-300 font-mono text-xs">
                      {inv.number || inv.id.slice(-8)}
                    </td>
                    <td className="py-3 px-3 text-gray-900 dark:text-white font-semibold">
                      {formatCurrency(inv.amount_due, inv.currency)}
                    </td>
                    <td className="py-3 px-3">
                      <span
                        className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                          inv.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : inv.status === 'open'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                        >
                          View <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      )}

      {/* Plan-TrialSignupLink.md §J — the per-code "Have a code?" redeem entry
          (RedeemCouponInline) was removed: the coupon-code model is replaced by
          the global admin signup link (trial granted at signup, no in-app code).
          The component and its client API method are now deleted; the redeem
          endpoint stays dormant server-side. */}

      {/* 7. LiveAvatar add-on — plan-dependent, hidden for a never-paid
          workspace until a plan is chosen. */}
      {/* Promotional-avatar expiry notice. Quiet by design — informational,
          not a warning, because nothing has gone wrong: the customer simply
          has an avatar on a clock that their paid plan does not extend.
          🚨 The target case LOOKS FINE — upgraded mid-trial, real paid plan,
          `liveavatar_addon.status: 'active'`, minutes bar normal — and the
          avatar still dies on day 7. Gated on the backend's date, which is
          null for a genuinely paid add-on and for Shopify's bundled avatar. */}
      {hasPlan && billing.liveavatar_promo_expires_at && (
        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 sm:px-5 py-3 sm:py-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-indigo-700 dark:text-indigo-300" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                {/* Never "Invalid Date" / "—": the sentence changes shape. */}
                {formatDateOrNull(billing.liveavatar_promo_expires_at)
                  ? `Your promotional AI Avatar ends on ${formatDateOrNull(billing.liveavatar_promo_expires_at)}`
                  : 'Your promotional AI Avatar ends when the promotion expires'}
              </p>
              <p className="text-xs text-indigo-800 dark:text-indigo-300 mt-0.5 leading-snug">
                This one came free with your promotion and runs on its own
                clock, so upgrading your plan does not extend it. To keep AI
                Avatar after that date, add the AI Avatar add-on below.
              </p>
            </div>
          </div>
        </div>
      )}

      {hasPlan && <LiveAvatarSection credits={creditsByResource.liveavatar_minutes ?? []} />}

      {/* Plan-InternalWorkspacePolicyV2.md §C.2 (2026-05-15) — the old
          InternalWorkspaceModal "Managed by admin" gate is removed;
          internal workspaces now flow through the same plan-change
          modal below, which detects `isInternalWorkspace` and swaps the
          title/CTA/body copy to the no-Stripe self-serve apply flow. */}

      {/* Cancel-subscription confirm modal. Plain English on purpose — the
          point of this screen is that the customer understands (a) nothing
          stops today, and (b) the AI Avatar add-on goes with the plan. */}
      {cancelOpen && (
        <ConfirmModal
          title="Cancel your plan"
          onClose={() => {
            if (cancelBusy) return;
            setCancelOpen(false);
          }}
          onConfirm={performCancelSubscription}
          confirming={cancelBusy}
          confirmLabel="Yes, cancel my plan"
          confirmColor="bg-red-600 hover:bg-red-700"
          closeLabel="Keep my plan"
        >
          <div className="space-y-4">
            <p className="leading-relaxed">
              Your plan stays active until{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {cancelEffectiveLabel}
              </span>
              . You keep your full limits until then — nothing changes today,
              and you will not be charged again.
            </p>

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <p className="font-semibold text-gray-900 dark:text-white mb-1.5">
                {cancelEffectiveDate
                  ? `What happens on ${cancelEffectiveDate}`
                  : 'What happens then'}
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                <li>Your plan ends and your agents stop answering customers.</li>
                <li>Paid features stop working for everyone on your team.</li>
                <li>To start again, you pick a plan from this page.</li>
              </ul>
            </div>

            {/* 🚨 The owner asked specifically that this be shown — but ONLY
                to customers whose avatar actually dies with the plan.
                `liveavatar_will_cancel_on_plan_cancel` is the backend's
                answer, derived from a real Stripe subscription item; a
                coupon-granted add-on survives the cancellation and must NOT
                see this. The forfeit sentence repeats the promise
                LiveAvatarSection already makes, word for word. */}
            {avatarWillCancel && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                  <Bot className="w-4 h-4 flex-shrink-0" />
                  Your AI Avatar add-on will be cancelled too
                </p>
                <p className="text-amber-800 dark:text-amber-300 mt-1 leading-relaxed">
                  AI Avatar is an add-on to your plan, so it cannot continue
                  without one. It ends on the same day
                  {cancelEffectiveDate ? (
                    <>
                      {' — '}
                      <span className="font-semibold">{cancelEffectiveDate}</span>
                    </>
                  ) : (
                    ''
                  )}
                  . Unused minutes are forfeited on cancellation — they do not
                  carry over.
                </p>
              </div>
            )}

            {hasScheduledChange && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {scheduledChangePlural
                    ? 'Your scheduled changes will be dropped'
                    : 'Your scheduled change will be dropped'}
                </p>
                <p className="text-amber-800 dark:text-amber-300 mt-1 leading-relaxed">
                  You have {scheduledChangeText || 'a change'} lined up for
                  your next billing date. Cancelling drops{' '}
                  {scheduledChangePlural ? 'them' : 'it'} for good. Clicking
                  “Keep my plan” later brings your plan back, but it will{' '}
                  <span className="font-semibold">not</span> bring{' '}
                  {scheduledChangePlural ? 'these changes' : 'this change'}{' '}
                  back — you would need to schedule{' '}
                  {scheduledChangePlural ? 'them' : 'it'} again.
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Changed your mind? You can turn the cancellation off any time
              before {cancelEffectiveLabel} with the “Keep my plan” button
              {hasScheduledChange
                ? ` — that brings your plan back, but not the scheduled ${scheduledChangePlural ? 'changes' : 'change'} above.`
                : '.'}
            </p>
          </div>
        </ConfirmModal>
      )}

      {/* Confirm modal */}
      {modalTarget && (
        <ConfirmModal
          title={
            isInternalWorkspace
              ? `Apply ${PLAN_DISPLAY[modalTarget].name} plan · ${billingInterval === 'year' ? 'Yearly' : 'Monthly'}`
              : isTrialCheckout
              ? `Start ${PLAN_DISPLAY[modalTarget].name} · ${billingInterval === 'year' ? 'Yearly' : 'Monthly'}`
              : targetIsUpgrade
              ? `Upgrade to ${PLAN_DISPLAY[modalTarget].name} · ${billingInterval === 'year' ? 'Yearly' : 'Monthly'}`
              : `Downgrade to ${PLAN_DISPLAY[modalTarget].name} · ${billingInterval === 'year' ? 'Yearly' : 'Monthly'}`
          }
          onClose={closeModal}
          onConfirm={confirmChange}
          confirming={confirming}
          confirmLabel={
            isInternalWorkspace
              ? 'Apply plan change'
              : isTrialCheckout
              ? 'Continue to checkout'
              : targetIsUpgrade
              ? 'Confirm upgrade'
              : 'Schedule downgrade'
          }
          confirmColor={
            isInternalWorkspace || isTrialCheckout || targetIsUpgrade
              ? 'bg-gradient-to-r from-primary to-purple-600 hover:shadow-lg'
              : 'bg-amber-600 hover:bg-amber-700'
          }
        >
          {isInternalWorkspace ? (
            // Plan-InternalWorkspacePolicyV2.md §C.2 — internal-workspace
            // self-serve apply. Backend (Storm Phase A.3) short-circuits
            // Stripe and returns `{status: "applied", plan: ...}` — no
            // checkout redirect, no proration. We show a stripped-down
            // confirmation body so the user knows it's instant + free.
            <div className="space-y-3">
              <p className="text-gray-700 dark:text-gray-300">
                Switch this workspace to{' '}
                <span className="font-semibold">
                  {PLAN_DISPLAY[modalTarget].name}{' '}
                  {billingInterval === 'year' ? 'Yearly' : 'Monthly'}
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
              </p>
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2.5 text-xs text-blue-800 dark:text-blue-200">
                Internal workspaces apply plan changes immediately and
                are never billed. Plan limits update as soon as you
                confirm — Stripe is not contacted.
              </div>
            </div>
          ) : isTrialCheckout ? (
            <div className="space-y-3">
              <p className="text-gray-700 dark:text-gray-300">
                You'll be redirected to Stripe's secure checkout to start your{' '}
                <span className="font-semibold">
                  {PLAN_DISPLAY[modalTarget].name}{' '}
                  {billingInterval === 'year' ? 'Yearly' : 'Monthly'}
                </span>{' '}
                subscription.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
              </p>
              <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Plan</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {PLAN_DISPLAY[modalTarget].name}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-gray-600 dark:text-gray-400">Billing</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {billingInterval === 'year'
                      ? `${PLAN_DISPLAY[modalTarget].yearlyPrice}/yr`
                      : `${PLAN_DISPLAY[modalTarget].price}/mo`}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Your trial ends immediately once payment succeeds. You can cancel or change
                plans anytime from this page.
              </p>
            </div>
          ) : (
          <>
          {previewLoading && (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating proration…
            </div>
          )}
          {previewError && !previewLoading && (
            <p className="text-red-600 dark:text-red-400">{previewError}</p>
          )}
          {preview && !previewLoading && (
            <div className="space-y-3">
              {targetIsUpgrade ? (
                <>
                  <p>
                    You'll be charged a prorated amount today and unlock{' '}
                    <span className="font-semibold">
                      {PLAN_DISPLAY[modalTarget].name}{' '}
                      {billingInterval === 'year' ? 'Yearly' : 'Monthly'}
                    </span>{' '}
                    immediately at{' '}
                    <span className="font-semibold">
                      {billingInterval === 'year'
                        ? `${PLAN_DISPLAY[modalTarget].yearlyPrice}/yr`
                        : `${PLAN_DISPLAY[modalTarget].price}/mo`}
                    </span>
                    .
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                  </p>
                  <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Amount due now</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(preview.amount_due, preview.currency)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-gray-600 dark:text-gray-400">Next billing date</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatDate(preview.period_end)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p>
                    Your plan will change to{' '}
                    <span className="font-semibold">
                      {PLAN_DISPLAY[modalTarget].name}{' '}
                      {billingInterval === 'year' ? 'Yearly' : 'Monthly'}
                    </span>{' '}
                    on{' '}
                    <span className="font-semibold">{formatDate(preview.period_end)}</span>. You keep
                    your current features until then and can undo anytime before the switch.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Workspace: <span className="font-semibold text-gray-700 dark:text-gray-200">{activeWorkspace?.name}</span>
                  </p>
                  {billing && (
                    <GuardRailCheck
                      currentUsage={billing.usage}
                      target={modalTarget}
                    />
                  )}
                </>
              )}
            </div>
          )}
          </>
          )}
        </ConfirmModal>
      )}
    </div>
  );
};

// ---------- downgrade guard display (soft warning Phase A) ----------
const GuardRailCheck: React.FC<{
  currentUsage: UsageSnapshot;
  target: Plan;
}> = ({ currentUsage, target }) => {
  // Phase A: count-only. We show a warning if current agents/articles/members
  // exceed what the target plan would allow, based on hard-coded v3 numbers.
  // Once backend returns target limits in preview, swap this out.
  const TARGET_LIMITS: Record<Plan, { agents: number; articles: number; team: number }> = {
    trial: { agents: 10, articles: 50, team: 30 },
    starter: { agents: 10, articles: 50, team: 30 },
    pro: { agents: 30, articles: 300, team: 100 },
    business: { agents: -1, articles: -1, team: 500 },
    enterprise: { agents: -1, articles: -1, team: -1 },
  };
  const t = TARGET_LIMITS[target];
  const violations: string[] = [];
  const agents = currentUsage.agents ?? 0;
  const articles = currentUsage.articles ?? 0;
  const members = currentUsage.team_members ?? 0;
  if (t.agents >= 0 && agents > t.agents) violations.push(`${agents} agents (allowed: ${t.agents})`);
  if (t.articles >= 0 && articles > t.articles) violations.push(`${articles} articles (allowed: ${t.articles})`);
  if (t.team >= 0 && members > t.team) violations.push(`${members} team members (allowed: ${t.team})`);
  if (violations.length === 0) return null;
  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
      <p className="font-semibold mb-1 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        Heads up — current usage exceeds {PLAN_DISPLAY[target].name}:
      </p>
      <ul className="list-disc list-inside space-y-0.5">
        {violations.map((v) => (
          <li key={v}>{v}</li>
        ))}
      </ul>
      <p className="mt-2">Phase A does not hard-block, but please reduce before the switch to avoid service disruption.</p>
    </div>
  );
};

// ---------- provider switch (build-time) ----------
// `IS_SHOPIFY_BUILD` is a constant baked in at build time, so each bundle
// only keeps the branch it uses: the stripe bundle keeps StripeBillingPage
// (full Stripe UI, unchanged), the shopify bundle keeps ShopifyBillingView
// (Managed Pricing) and tree-shakes the Stripe surface away.
const BillingPage: React.FC = () =>
  IS_SHOPIFY_BUILD ? <ShopifyBillingView /> : <StripeBillingPage />;

export default BillingPage;
